import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, BanknoteIcon, Coins, Landmark } from "lucide-react";
import api from "../lib/api";

function StatusPill({ p }) {
  const label =
    p.status === "pending_payment" ? "awaiting approval" : p.status === "paid" && p.method === "credit" ? "issued" : p.status;
  const c =
    p.status === "paid"
      ? { bg: "rgba(94,234,212,0.08)", bd: "rgba(94,234,212,0.35)", c: "#5EEAD4" }
      : p.status === "cancelled"
        ? { bg: "rgba(239,68,68,0.08)", bd: "rgba(239,68,68,0.35)", c: "#EF4444" }
        : { bg: "rgba(247,181,0,0.08)", bd: "rgba(247,181,0,0.35)", c: "#F7B500" };
  return (
    <span
      className="pg-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm"
      style={{ background: c.bg, border: `1px solid ${c.bd}`, color: c.c }}
    >
      {label}
    </span>
  );
}

export default function AdminPayouts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/affiliate-payouts");
      setRows(data || []);
    } catch {
      toast.error("Could not load cash-outs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markPaid = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/affiliate-payouts/${id}/mark-paid`);
      toast.success("Approved & marked paid — the affiliate has been emailed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark paid");
    } finally {
      setBusy(null);
    }
  };

  const awaiting = rows.filter((r) => r.status === "pending_payment");
  const totalAwaiting = awaiting.reduce((a, r) => a + (r.amount_gbp || 0), 0);
  const totalCredit = rows.filter((r) => r.method === "credit").reduce((a, r) => a + (r.amount_gbp || 0), 0);

  return (
    <div data-testid="admin-payouts-page">
      <div className="mb-6">
        <div className="pg-micro mb-2">// Admin · Affiliate cash-outs</div>
        <h1 className="text-[#F7FAFC] text-3xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
          Cash-out requests
        </h1>
        <p className="text-[#9CA8B8] text-sm mt-1 max-w-3xl">
          Affiliates cash out their available pot themselves. Store credit is issued instantly (nothing to do here).
          Bank transfers land below as <strong className="text-[#F7FAFC]">awaiting approval</strong>: send the money
          to the details shown, then click <strong className="text-[#F7FAFC]">Approve &amp; mark paid</strong> — the
          affiliate gets the confirmation email automatically (3–5 working days to land).
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
        <div className="pg-card p-5">
          <div className="pg-micro mb-2 flex items-center gap-2"><Landmark size={12} /> // Awaiting your approval</div>
          <div className="text-3xl pg-heading text-[#F7B500]" style={{ fontFamily: "'Rajdhani'" }} data-testid="admin-payouts-total-awaiting">
            £{totalAwaiting.toFixed(2)}
          </div>
          <div className="text-[#9CA8B8] text-xs mt-1">{awaiting.length} request{awaiting.length === 1 ? "" : "s"}</div>
        </div>
        <div className="pg-card p-5">
          <div className="pg-micro mb-2 flex items-center gap-2"><Coins size={12} /> // Store credit issued</div>
          <div className="text-3xl pg-heading text-[#A6FF00]" style={{ fontFamily: "'Rajdhani'" }}>£{totalCredit.toFixed(2)}</div>
        </div>
        <div className="pg-card p-5">
          <div className="pg-micro mb-2 flex items-center gap-2"><BanknoteIcon size={12} /> // Cash-outs on file</div>
          <div className="text-3xl pg-heading text-[#F7FAFC]" style={{ fontFamily: "'Rajdhani'" }}>{rows.length}</div>
        </div>
      </div>

      <div className="pg-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#9CA8B8] text-sm">
            <Loader2 size={18} className="animate-spin text-[#00C2D6] mx-auto mb-3" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-[#9CA8B8] text-sm">No cash-outs yet.</div>
        ) : (
          <>
          <ul className="md:hidden divide-y divide-[#1E2E44]" data-testid="admin-payouts-cards">
            {rows.map((p) => {
              const a = p.affiliate || {};
              return (
                <li key={p.id} className="p-4" data-testid={`admin-payout-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[#F7FAFC] text-sm truncate">{a.name || p.affiliate_slug}</div>
                      <div className="text-[#9CA8B8] text-xs">
                        {(p.requested_at || p.created_at || "").slice(0, 10)} · {p.method === "credit" ? "Store credit ×1.5" : "Bank transfer"}
                      </div>
                      {p.voucher_code && <div className="text-[#A6FF00] pg-mono text-[11px] mt-0.5 break-all">{p.voucher_code}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[#F7FAFC] font-semibold">£{Number(p.amount_gbp).toFixed(2)}</div>
                      <div className="text-[#9CA8B8] text-[11px]">pot £{Number(p.pot_gbp ?? p.amount_gbp).toFixed(2)}</div>
                    </div>
                  </div>
                  {p.method === "bank" && (
                    <div className="mt-2 text-[#F7FAFC] pg-mono text-[11px] leading-relaxed">
                      {a.bank_account_holder ? (
                        <>{a.bank_account_holder} · {a.bank_sort_code && a.bank_account_number ? `${a.bank_sort_code} · ${a.bank_account_number}` : a.bank_iban || "—"}</>
                      ) : (
                        <span className="text-[#EF4444]">No bank details</span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 mt-3">
                    <StatusPill p={p} />
                    {p.status === "pending_payment" ? (
                      <button onClick={() => markPaid(p.id)} disabled={busy === p.id} className="pg-btn-primary text-[10px] uppercase tracking-wider" data-testid={`admin-payout-mark-paid-m-${p.id}`}>
                        {busy === p.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve &amp; mark paid
                      </button>
                    ) : (
                      <span className="text-[#9CA8B8] text-xs pg-mono">{p.paid_at ? (p.paid_at || "").slice(0, 10) : "—"}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="pg-micro text-left border-b border-[#1E2E44]">
                  <th className="p-3">Requested</th>
                  <th className="p-3">Affiliate</th>
                  <th className="p-3">Method</th>
                  <th className="p-3">Pot</th>
                  <th className="p-3">Amount</th>
                  <th className="p-3">Pay to</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const a = p.affiliate || {};
                  return (
                    <tr key={p.id} className="border-b border-[#1E2E44]/50" data-testid={`admin-payout-row-${p.id}`}>
                      <td className="p-3 text-[#9CA8B8] text-xs">{(p.requested_at || p.created_at || "").slice(0, 10)}</td>
                      <td className="p-3">
                        <div className="text-[#F7FAFC] text-xs">{a.name || "—"}</div>
                        <div className="text-[#A6FF00] pg-mono text-[11px]">{p.affiliate_slug}</div>
                      </td>
                      <td className="p-3 text-[#9CA8B8] text-xs">
                        {p.method === "credit" ? "Store credit ×1.5" : "Bank transfer"}
                        {p.voucher_code && <div className="text-[#A6FF00] pg-mono text-[11px] mt-0.5">{p.voucher_code}</div>}
                      </td>
                      <td className="p-3 text-[#9CA8B8]">£{Number(p.pot_gbp ?? p.amount_gbp).toFixed(2)}</td>
                      <td className="p-3 text-[#F7FAFC] font-semibold">£{Number(p.amount_gbp).toFixed(2)}</td>
                      <td className="p-3 text-xs">
                        {p.method === "bank" ? (
                          a.bank_account_holder ? (
                            <div className="text-[#F7FAFC] pg-mono text-[11px] leading-relaxed" data-testid={`admin-payout-bank-${p.id}`}>
                              {a.bank_account_holder}
                              <br />
                              {a.bank_sort_code && a.bank_account_number
                                ? `${a.bank_sort_code} · ${a.bank_account_number}`
                                : a.bank_iban || "—"}
                            </div>
                          ) : (
                            <span className="text-[#EF4444]">No bank details</span>
                          )
                        ) : (
                          <span className="text-[#9CA8B8]">—</span>
                        )}
                      </td>
                      <td className="p-3"><StatusPill p={p} /></td>
                      <td className="p-3">
                        {p.status === "pending_payment" ? (
                          <button
                            onClick={() => markPaid(p.id)}
                            disabled={busy === p.id}
                            className="pg-btn-primary text-[10px] uppercase tracking-wider"
                            data-testid={`admin-payout-mark-paid-${p.id}`}
                          >
                            {busy === p.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                            Approve &amp; mark paid
                          </button>
                        ) : (
                          <span className="text-[#9CA8B8] text-xs pg-mono">{p.paid_at ? (p.paid_at || "").slice(0, 10) : "—"}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
